import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MapIcon from "@mui/icons-material/Map";
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { noop } from "lodash";
import { Link, useNavigate } from "react-router-dom";
import type { UserRole } from "../auth/auth";
import {
  storageUrlWithCredentials,
  useStorageUrlWithCredentials,
} from "../components/imagesPreview/storageUrlWithCredentials";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import { OrderProducts } from "../orders2/orderCard/OrderProducts";
import { CardMediaImg } from "../publicPackages2/CardImage";
import { packageCountries, packageGroupTypes } from "../publicPackages2/packageTagsInfo";
import { TransportationIcon, TransportationName } from "../publicPackages2/transportation";
import type { ECommerceOffer, ECommerceProduct } from "./ECommerceOffers";
import { BookOfferButton } from "./details/BookOfferButton";
import { useProductDateRangeLabel } from "./productDateRangeLabel";

interface ECommerceListCardProps {
  readonly value: ECommerceOffer;
  readonly role?: UserRole;
  readonly selectRoute: (value: ECommerceProduct[]) => void;
  readonly onContextMenu: () => void;
  readonly onMapClick: (url: string) => void;
}

function countUniqueDates(products: ECommerceProduct[]): number {
  return new Set(products.map((product) => dayjs(product.date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT))).size;
}

export function ECommerceListCard(props: ECommerceListCardProps) {
  const offer = props.value;
  const role = props.role;
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const navigate = useNavigate();

  const products = offer.products;
  const uniqueDatesCount = countUniqueDates(products);
  const mapThumbnailUrl = useStorageUrlWithCredentials(offer.map?.thumbnail.url);

  return (
    <Card
      key={offer.id.value}
      onContextMenu={isAdminOrAgent ? props.onContextMenu : noop}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.05)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
        },
        "&:hover .trip-map-overlay": {
          opacity: 1,
        },
        "&:hover .trip-base-image": {
          transform: "scale(1.03)",
        },
      }}
    >
      <Box sx={{ flex: "1 1 60%", display: "flex", flexDirection: { xs: "column", sm: "row" } }}>
        <Box
          onClick={() => navigate(`/e-commerce/${offer.id.value}`)}
          sx={{
            width: { xs: "100%", sm: 280 },
            flexShrink: 0,
            position: "relative",
            cursor: "pointer",
            overflow: "hidden",
          }}
        >
          <CardMediaImg
            sx={{ height: { xs: 200, sm: "100%" }, objectFit: "cover" }}
            imageUrl={offer.image?.thumbnail.url}
            alt={offer.name}
            className="trip-base-image"
          />
          {mapThumbnailUrl && (
            <Box
              className="trip-map-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                transition: "opacity 0.25s ease",
                bgcolor: "rgba(0,0,0,0.18)",
              }}
            >
              <Box
                component="img"
                src={mapThumbnailUrl}
                alt={`Mapa ${offer.name}`}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.15))",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    storageUrlWithCredentials(offer.map?.www.url).then((url) => {
                      if (url) {
                        props.onMapClick(url);
                      }
                    });
                  }}
                  sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: "bold" }}
                >
                  Pokaż mapę
                </Button>
              </Box>
            </Box>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 12, left: 12, right: 12, justifyContent: "space-between" }}
          >
            <Chip
              label={useProductDateRangeLabel(offer.products) || "Termin do ustalenia"}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.65)",
                color: "#fff",
                fontWeight: 600,
                maxWidth: "calc(100% - 92px)",
                "& .MuiChip-label": { fontSize: "0.8rem" },
              }}
            />
            <Chip
              label={`ID: ${offer.id.userFriendly}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.9)", fontWeight: "bold", flexShrink: 0 }}
            />
          </Stack>
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            onClick={() => navigate(`/e-commerce/${offer.id.value}`)}
            sx={{ lineHeight: 1.3, mb: 1, fontWeight: "bold", cursor: "pointer" }}
          >
            {offer.name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 3,
              whiteSpace: "pre-line",
            }}
          >
            {offer.info}
          </Typography>

          <Stack direction="row" sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
            {packageCountries(offer).map((c) => (
              <Chip
                key={c}
                label={c}
                size="small"
                sx={{ bgcolor: "rgba(13, 110, 253, 0.08)", color: "primary.main", fontWeight: 500 }}
              />
            ))}
            {packageGroupTypes(offer).map((g) => (
              <Chip key={g} label={g} size="small" variant="outlined" sx={{ borderColor: "rgba(0,0,0,0.12)" }} />
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={3}
            sx={{ mb: 3, pt: 2, borderTop: "1px solid rgba(0,0,0,0.08)", color: "text.secondary" }}
          >
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {offer.days} dni
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              <TransportationIcon forPackage={offer} />
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {" "}
                <TransportationName forPackage={offer} />
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: "auto" }}>
            <Box sx={{ mt: "auto", display: "flex" }}>
              <Button
                variant="outlined"
                color="primary"
                sx={{ py: 1, px: 3, borderRadius: 2 }}
                component={Link}
                to={`/e-commerce/${offer.id.value}`}
              >
                Szczegóły
              </Button>
            </Box>
            <BookOfferButton offer={offer} />
          </Stack>
        </CardContent>
      </Box>
      <Box
        sx={{
          flex: "0 0 40%",
          borderLeft: { lg: "1px solid rgba(0,0,0,0.08)" },
          borderTop: { xs: "1px solid rgba(0,0,0,0.08)", lg: "none" },
          p: 3,
          bgcolor: "rgba(0,0,0,0.01)",
        }}
      >
        <Stack direction="row" spacing={1} sx={{ mb: 3, alignItems: "center" }}>
          <MapIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
            Trasa wycieczki
          </Typography>
        </Stack>

        {products && products.length > 0 ? (
          <Box sx={{ position: "relative", ml: 1 }}>
            <Stack spacing={2.5}>
              <OrderProducts values={products} limit={4} />
              {uniqueDatesCount > 4 && (
                <Box sx={{ position: "relative", zIndex: 1, pt: 1, pl: 5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => props.selectRoute(offer.products)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: "bold" }}
                  >
                    Rozwiń pełny plan ({uniqueDatesCount} dni)
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
            Skonsultuj się z opiekunem w celu poznania trasy.
          </Typography>
        )}
      </Box>
    </Card>
  );
}
